/**
 * A recusa do servidor, traduzida pelo motivo e não pelo texto — §4.3 e V5 da spec #408.
 *
 * O backend devolve 403 com `motivo` (`permissao`, `escopo`, `modulo` ou `contexto`). São
 * quatro situações diferentes para quem está na tela: uma pede outro perfil, outra pede trocar
 * de contexto no cabeçalho, outra é comercial. Mostrar "acesso negado" para as quatro é o que
 * faz a pessoa reabrir a mesma tela esperando resultado diferente.
 */

import { ApiRequestError, isApiError } from '../types/common';
import type { MotivoDeRecusa } from '../types/manifesto';

const MOTIVOS: readonly MotivoDeRecusa[] = [
  'permissao',
  'escopo',
  'modulo',
  'contexto',
  'plataforma',
];

const MENSAGEM: Readonly<Record<MotivoDeRecusa, string>> = {
  permissao:
    'Seu perfil não inclui esta ação. Peça a quem administra os perfis para incluí-la no seu.',
  escopo:
    'Este registro pertence a outro contexto. Troque de empresa ou clínica no topo da tela para operá-lo.',
  modulo:
    'O módulo que cobre esta ação está fora do contrato deste contexto. A leitura continua; para reativar, fale com o time comercial.',
  contexto:
    'Sua sessão está sem contexto ativo. Escolha a empresa ou a clínica em que você vai operar.',
  // Nem permissão nem escopo resolvem esta: o cadastro é o mesmo para todas as empresas, e
  // ter a permissão ou trocar de empresa não muda de quem ele é.
  plataforma:
    'Este cadastro é compartilhado por todas as empresas: alterá-lo é ação de plataforma. Aqui ele é somente leitura.',
};

function leCampo(detalhes: Record<string, unknown>, campo: string): unknown {
  if (detalhes[campo] !== undefined) return detalhes[campo];
  const aninhado = detalhes.details;
  if (typeof aninhado === 'object' && aninhado !== null) {
    return (aninhado as Record<string, unknown>)[campo];
  }
  return undefined;
}

/** O motivo direto do corpo da resposta, para quem ainda não tem um `ApiError` montado. */
export function motivoNoCorpo(corpo: unknown): MotivoDeRecusa | undefined {
  if (typeof corpo !== 'object' || corpo === null) return undefined;

  const motivo = leCampo(corpo as Record<string, unknown>, 'motivo');
  return MOTIVOS.includes(motivo as MotivoDeRecusa) ? (motivo as MotivoDeRecusa) : undefined;
}

export function motivoDaRecusa(erro: unknown): MotivoDeRecusa | undefined {
  if (!isApiError(erro)) return undefined;
  return motivoNoCorpo(erro.details);
}

export function mensagemDeRecusa(motivo: MotivoDeRecusa): string {
  return MENSAGEM[motivo];
}

/**
 * O texto de uma falha, com a recusa de acesso na frente da mensagem genérica da tela.
 *
 * Existe porque o `catch` da tela costuma ler `err.response.data.error`, e a recusa não chega
 * por esse caminho: o cliente HTTP já a traduziu num objeto sem `response`. O resultado era a
 * tela dizer "Falha ao gerar a Guia" sobre um 403 que sabia exatamente o que tinha faltado.
 */
export function mensagemDeFalha(erro: unknown, alternativa: string): string {
  const motivo = motivoDaRecusa(erro);
  if (motivo) return mensagemDeRecusa(motivo);

  if (isApiError(erro) && erro.message) return erro.message;
  if (erro instanceof Error && erro.message) return erro.message;
  return alternativa;
}

/** A falha ao abrir um registro por id, com o status que separa sem-acesso de inexistente. */
export interface FalhaDeCarga {
  status?: number;
  mensagem: string;
}

export function falhaDeCarga(erro: unknown, alternativa: string): FalhaDeCarga {
  return {
    status: erro instanceof ApiRequestError ? erro.status : undefined,
    mensagem: mensagemDeFalha(erro, alternativa),
  };
}

/**
 * O texto do `AvisoDeFalhaAoCarregar`. O 404 do servidor chega em inglês e técnico; a tela
 * sabe dizer o que não foi encontrado.
 */
export function textoDaFalhaDeCarga(falha: FalhaDeCarga | null, naoEncontrado: string): string {
  if (!falha || falha.status === 404) return naoEncontrado;
  return falha.mensagem;
}
